/**
 * Notes. Because reducers use Immer, you can't just console.log pieces of your store. You'll get a bunch of proxy
 * stuff from the WritableDraft<> template. Wrap your data in unImmer() to print it out normally.
 */

import { RouterClient } from "../../../../clients/routerClient";
import { ActionReducerMapBuilder, unImmer, CMD } from "../../../../common/redux/createReducer";
import { actions } from "./actions";
import {
	WindowServiceStore,
	WindowServiceMainStore,
	WindowDescriptor,
	TitleBarData,
	TabCollection,
	WindowTitleBarStore,
	WindowDescriptorScrubbed,
} from "../types";
import { setUserDefinedTitle, promoteTabWindow } from "./windowTitleBar";
import { getRemoteReduxStore } from "../../common/store";
import { PhysicalWindowStateEvent, Rectangle, WindowState } from "../../../../FEA";
import { Logger } from "../../../../clients/logger";
import { StorageClient } from "../../../../clients/storageClient";
import { camelCase, isEqualIgnoringArrayOrder } from "../../../../common/util";
import { getMonitorByBounds } from "../../../../common/Monitors";
import { WindowServiceStoreInstance } from "../localTypes";
import { System } from "../../../../common/system";

/**
 * This is safe to use in any function that is an effect from a reducer, since the store is guaranteed to
 * already exist. We use this instead of the getStore() from ../store.ts in order to avoid a circular dependency.
 */
const getStore = () => getRemoteReduxStore<WindowServiceStoreInstance>("@@finsemble/remoteRedux/WindowServiceStore");

type DockingManagerRegisterOverrides = {
	allowAutoArrange: boolean;
	snapping: boolean;
	tiling: boolean;
	tabbing: boolean;
	canGroup: boolean;
	canMinimize: boolean;
	canMaximize: boolean;
	overwriteStartDocked: boolean;
	maxHeight?: number | null;
	maxWidth?: number | null;
	minHeight?: number | null;
	minWidth?: number | null;
	showTaskbarIcon?: boolean | null;
};

const getInitialState = (): WindowServiceMainStore => {
	return {
		windows: {},
		launchData: {},
		groups: {},
		maximization: {},
		workspaceClosings: [],
		dockingOperation: {
			type: null,
		},
	};
};

const unregisterWithDocking = async (windowName: string) => {
	RouterClient.transmit("DockingService.deregisterWindow", {
		name: windowName,
	});
};

// Generic actions that we might want to decide if a window is allowed to do
type WindowActionPermissionType = "grouping" | "tabbing" | "minimize" | "maximize" | "close" | "alwaysOnTop" | "clone";

// A map of unsubscribes from calls to listenForDNDUpdates
let dndUnsubscribes: Record<string, ReturnType<typeof RouterClient.subscribe>> = {};

type WindowServiceActions =
	| "bringToFront"
	| "close"
	| "focus"
	| "getOptions"
	| "hide"
	| "maximize"
	| "minimize"
	| "restore"
	| "setAlwaysOnTop"
	| "setBounds"
	| "setComponentState"
	| "show";

/**
 * This function sends a request to the window service to act on an individual window. This uses
 * the same interface that is used by FinsembleWindow.
 *
 * Note: the local store is unaffected by these actions until they occur and are picked up
 * as events by FEA in this store's "main" module.
 *
 * @param windowName The name of the window to act upon
 * @param command The requested command
 */
export const sendCommandToWindowService = async (
	windowName: string,
	command: WindowServiceActions,
	params?: Record<string, any>
) => {
	const channelName = `WindowService-Request-${command}`;
	const { err, response } = await RouterClient.query(channelName, {
		windowIdentifier: {
			windowName,
		},
		...params,
	});
	if (err || !response) {
		Logger.system.debug(`window service:sendCommandToWindowService() ${channelName} error`, err);
	}
	return { err, data: response.data };
};

/**
 * When the DragAndDropClient is used it sends out a router message to indicate that the ShareButton
 * should be displayed. This function listens for those notices and dispatches actions to turn
 * the ShareButton on or off. This function is called whenever a window is created.
 */
const listenForDNDUpdates = (windowName: string) => {
	const unsubscribeID = RouterClient.subscribe(`${windowName}Finsemble.Sharer`, (err, response) => {
		if (err) return;
		if (typeof response.data.emitterEnabled === "undefined") return;
		const store = getStore();
		store?.dispatch(
			actions.enableDND({
				targetWindowName: windowName,
				toggleState: response.data.emitterEnabled,
			})
		);
	});
	dndUnsubscribes[windowName] = unsubscribeID;
};

/**
 * Unsubscribes from dnd subscription events. This is called when a window is closed.
 */
const unsubscribeFromDNDUpdate = (windowName: string) => {
	const unsubscribeID = dndUnsubscribes[windowName];
	if (unsubscribeID) {
		RouterClient.unsubscribe(unsubscribeID);
		delete dndUnsubscribes[windowName];
	}
};

/**
 * True if any specific window management features are enabled for a given component's config
 */
const anyWindowManagementAllowed = (foreignWindowServiceConfig: any): boolean =>
	foreignWindowServiceConfig?.allowSnapping ||
	foreignWindowServiceConfig?.allowGrouping ||
	foreignWindowServiceConfig?.allowAutoArrange ||
	foreignWindowServiceConfig?.allowTabbing ||
	foreignWindowServiceConfig?.allowTiling ||
	foreignWindowServiceConfig?.allowMinimize ||
	foreignWindowServiceConfig?.allowMaximize;

/**
 * Registers windows with the docking system. A window is registered if it needs to be managed for snap and dock.
 * This is determined by examining all of the window's configurations.
 */
const maybeRegisterWithDocking = async (windowName: string, descriptor: WindowDescriptor) => {
	const myConfig = descriptor.customData;

	const windowServiceConfig = myConfig?.foreign?.services?.windowService;
	const anyAllowIsTrue = anyWindowManagementAllowed(windowServiceConfig);

	// default value in the Schema is `true`
	const isDockable = !!myConfig?.window?.dockable;
	const FSBLHeader = myConfig?.foreign?.components?.["Window Manager"]?.FSBLHeader;

	// If we can manipulate window (allow*) OR manage movement OR window is dockable OR window has FSBLHeader then register for docking.
	const shouldRegisterDocking =
		anyAllowIsTrue || isDockable || FSBLHeader || windowServiceConfig?.manageWindowMovement === true;

	if (!shouldRegisterDocking || windowServiceConfig?.manageWindowMovement === false) return;

	/**
	 * manageMovement dictates generically whether this is a window that the user can manipulate. This is true if
	 * manageWindowMovement is not set explicitly to `false`, or if there is an FSBLHeader (windowTitleBar) or if the window is explicitly dockable.
	 */
	const defaultValue = windowServiceConfig?.manageWindowMovement ?? (FSBLHeader || isDockable) ?? true;
	const registerMessage: DockingManagerRegisterOverrides = {
		allowAutoArrange: windowServiceConfig?.allowAutoArrange ?? defaultValue,
		// Ephemeral windows are not allowed to snap
		snapping: !myConfig?.window?.ephemeral && (windowServiceConfig?.allowSnapping ?? defaultValue),
		tiling: windowServiceConfig?.allowTiling ?? defaultValue,
		tabbing: windowServiceConfig?.allowTabbing ?? defaultValue,
		canGroup: windowServiceConfig?.allowGrouping ?? defaultValue,
		canMinimize: windowServiceConfig?.allowMinimize ?? myConfig?.component?.canMinimize ?? defaultValue,
		canMaximize: windowServiceConfig?.allowMaximize ?? myConfig?.component?.canMaximize ?? defaultValue,
		// Determines whether a dockable component should retrieve its state from memory, or start with default (config defined) options every time
		overwriteStartDocked: myConfig?.foreign?.services?.workspaceService?.global ?? false,
		// -1 is sometimes set to mean no max/min
		maxWidth: descriptor.maxWidth == -1 ? null : (descriptor.maxWidth ?? null),
		maxHeight: descriptor.maxHeight == -1 ? null : (descriptor.maxHeight ?? null),
		minWidth: descriptor.minWidth == -1 ? null : (descriptor.minWidth ?? null),
		minHeight: descriptor.minHeight == -1 ? null : (descriptor.minHeight ?? null),
		showTaskbarIcon: descriptor.showTaskbarIcon ?? null,
	};

	const options = {
		...myConfig?.window,
		...registerMessage,
	};

	/**
	 * Only register web windows (FEA) with the docking service. Native apps currently send this message
	 * directly, even freestanding native apps. If we were to send a message here then it would (a) be a
	 * duplicate and (b) be too late, because it's currently this registration message that causes the native
	 * window to be added to the docking service (for web windows, registration for docking is a separate step
	 * from adding the window to the DockingPoolSingleton. For native windows, it's a single step.) This can
	 * be cleaned up during window refactor.
	 *
	 * Any window without a windowType is also allowed through. This can occur for unknown windows.
	 */
	const { windowType = "WebWindow" } = descriptor;
	if (windowType === "WebWindow") {
		await RouterClient.query("DockingService.registerWindow", {
			name: windowName,
			options,
			windowType,
		});
	}
};

/**
 * Reverses a boolean value, but only if it's actually set. undefined or null are passed through.
 */
const reverseBoolean = (value?: boolean | null): boolean | undefined | null => {
	if (value === false) return true;
	if (value === true) return false;
	return value;
};

/**
 * Retrieves the request permission for a window. Returns true if the permission is not explicitly set or if data is missing.
 * This assumes that the window's config is available via its window descriptor which is in the store. This will not take into
 * consideration any dynamic config changes.
 */
export const getPermission = (
	state: WindowServiceStore,
	windowName: string,
	permission: WindowActionPermissionType
) => {
	const windowDescriptor = state.main.launchData[windowName]?.windowDescriptor ?? null;
	const myWindowConfig = windowDescriptor?.customData?.window;
	const foreign = windowDescriptor?.customData?.foreign;
	// Current permissions (dockingService is deprecated)
	const foreignWindowService = foreign?.services?.windowService ?? foreign?.services?.dockingService;
	// Legacy permissions
	const windowManager = foreign?.components?.["Window Manager"];
	const fsblHeader = windowManager?.FSBLHeader;
	// finsembleConfig
	const { finsembleConfig } = state;
	const globalServiceConfig = finsembleConfig?.servicesConfig;
	const legacyDockingService = globalServiceConfig?.docking;
	const globalWindowService =
		globalServiceConfig?.windowService ?? globalServiceConfig?.dockingService ?? legacyDockingService;

	const globalWindowManager = finsembleConfig?.["Window Manager"];

	if (permission === "close") return reverseBoolean(fsblHeader?.hideClose) ?? true;
	if (permission === "maximize")
		return foreignWindowService?.allowMaximize ?? reverseBoolean(fsblHeader?.hideMaximize) ?? true;
	if (permission === "minimize")
		return foreignWindowService?.allowMinimize ?? reverseBoolean(fsblHeader?.hideMaximize) ?? true;
	if (permission === "alwaysOnTop")
		return windowManager?.alwaysOnTopIcon ?? globalWindowManager?.alwaysOnTopIcon ?? false;
	if (permission === "clone") return windowManager?.cloneIcon ?? globalWindowManager?.cloneIcon ?? false;

	/**
	 * These permission checks are in two parts, separated by &&. The left hand side determines whether the feature is turned on
	 * or off globally. The right hand side then determines if the feature is turned on for that app. There is a long history
	 * of legacy permissions that are accounted for in this lengthy boolean.
	 */
	if (permission === "grouping") {
		// console.error("check permission grouping", windowName, foreignWindowService)
		return (
			(foreignWindowService?.manageWindowMovement ?? legacyDockingService?.enabled ?? true) &&
			(myWindowConfig?.addToWorkspace ?? foreignWindowService?.allowGrouping ?? myWindowConfig?.canDock ?? true)
		);
	}
	if (permission === "tabbing")
		return (
			(foreignWindowService?.manageWindowMovement ??
				globalWindowService?.tabbing?.enabled ??
				legacyDockingService?.tabbing?.enabled ??
				true) &&
			(foreignWindowService?.allowTabbing ??
				windowManager?.showTabs ??
				globalWindowManager?.showTabs ??
				reverseBoolean(foreignWindowService?.ignoreTilingAndTabbingRequests) ??
				true)
		);
	return false;
};

/**
 * Find the tab collection for the given window
 * @returns - The tab collection for the window, or null if the window isn't in a tab collection
 */
export const getTabCollection = (state: WindowServiceStore, windowName: string): TabCollection | null => {
	const tabCollectionId = state.windowTitleBar.windows[windowName]?.tabCollectionId;
	return tabCollectionId ? (state.windowTitleBar.tabCollections[tabCollectionId] ?? null) : null;
};

/**
 * Returns the ID for the first tab collection that contains the window. This is used when configuring
 * a windowTitleBar entry for a new window that is pre-destined for a given tab collection, such as
 * when restoring from workspaces.
 */
export const findTabCollectionId = (state: WindowServiceStore, windowName: string): string | null =>
	Object.values(state.windowTitleBar.tabCollections).reduce(
		(prev: string | null, collection: TabCollection) =>
			collection.tabs.includes(windowName) ? collection.tabCollectionId : prev,
		null
	);

let throttles: Record<string, ReturnType<typeof setTimeout>> = {};

const keyedThrottle = (key: string, func: Function, delay: number) => {
	if (throttles[key]) {
		clearTimeout(throttles[key]);
	}
	throttles[key] = setTimeout(() => {
		func();
		delete throttles[key];
	}, delay);
};

/**
 * Whenever the promoted window is relocated we must tell the workspace to update its corresponding
 * StackedWindow. This is only necessary to support customers who wish to revert to earlier versions
 * of Finsemble before tabbing refactor (pre 8.4).
 *
 * This code can be eliminated when all customers have been successfully migrated to 8.4 with no
 * need to revert.
 * @deprecated
 */
const updateStackInWorkspace = async (tabCollectionId: string, bounds: Rectangle) => {
	RouterClient.query("WorkspaceService.updateStackedWindow", {
		tabCollectionId,
		bounds,
	});
};

/**
 * Returns the bounds if the window is, or would be, maximized. Note that on MS Windows, Electron
 * returns bounds for maximized windows that are larger than the available screen size. This function
 * reduces these bounds to the accurate size.
 */
export const getMaximizedBounds = async (bounds: Rectangle): Promise<Rectangle> => {
	const monitor = await getMonitorByBounds({
		left: bounds.x,
		top: bounds.y,
		right: bounds.x + bounds.width,
		bottom: bounds.y + bounds.height,
		width: bounds.width,
		height: bounds.height,
	});

	return {
		x: monitor.availableRect.left,
		y: monitor.availableRect.top ?? 0,
		width: monitor.availableRect.width ?? 0,
		height: monitor.availableRect.height ?? 0,
	};
};

/**
 * Sets the bounds of an entire tab collection's hidden windows to be the same as the given window
 * (which should be the promoted window). A throttling function is used to make sure this only gets
 * done once (500 milliseconds after the sequence of mouse events ends).
 */

const throttledSetBounds = async (windowName: string, bounds: Rectangle, windowState: WindowState) => {
	/**
	 * When resizing into a maximized state we must get the actual size of the monitor. This is because
	 * the bounds returned by a resize event are incorrect on MS Windows for maximized windows (they are off
	 * by 7 pixels in each direction)
	 */
	const resizeBounds = windowState === "maximized" ? await getMaximizedBounds(bounds) : bounds;

	keyedThrottle(
		`setBounds-${windowName}`,
		() => {
			const store = getStore();
			if (!store) return;
			const state = store.getState();
			const collection = getTabCollection(state, windowName);
			if (!collection) return;
			updateStackInWorkspace(collection.tabCollectionId, resizeBounds);
			collection.tabs.forEach(async (tab) => {
				if (tab === windowName) return;
				// If any windows in the tab collection are OS maximized then unmaximize them first
				// And since unmaximizing will cause them to be visible, we then must re-hide them
				//
				// Should be executed only one time for the promoted tab otherwise could be an inconsistent state
				// where the required promoted tab could be hidden.
				if (
					state.main.windows[tab].physicalWindowState.windowState === "maximized" &&
					windowName === collection.promotedWindowName
				) {
					await sendCommandToWindowService(tab, "restore");
					await sendCommandToWindowService(tab, "hide");
				}
				sendCommandToWindowService(tab, "setBounds", {
					bounds: {
						top: resizeBounds?.y,
						left: resizeBounds?.x,
						width: resizeBounds?.width,
						height: resizeBounds?.height,
					},
				});
			});
		},
		500
	);
};

const identicalBounds = (left?: Rectangle, right?: Rectangle) =>
	left?.x === right?.x && left?.y === right?.y && left?.width === right?.width && left?.height === right?.height;

/**
 * Convenience function to determine if a given window is a hidden tab.
 */
export const isHiddenTab = (state: WindowServiceStore, windowName: string): boolean => {
	const tabCollectionId = state.windowTitleBar.windows[windowName]?.tabCollectionId;
	if (!tabCollectionId) return false;
	return state.windowTitleBar.tabCollections[tabCollectionId]?.promotedWindowName !== windowName;
};

/**
 * Returns all of the non-hidden tabs that are members of the same group as the requested window. This
 * is used for reactive events on groups.
 */
const getPeers = (state: WindowServiceStore, windowName: string): string[] => {
	const groupMemberships = Object.values(state.main.groups).filter(
		(group) => group.isGrouped && group.windowNames.includes(windowName)
	);
	const peers = groupMemberships.flatMap((group) => group.windowNames.filter((w) => w !== windowName));
	return peers.filter((w) => !isHiddenTab(state, w));
};

/**
 * Keep the window title bar’s "maximized" state in sync with the actual
 * physical window state, but only when the change comes from a resize event.
 */
const maybeUpdateMaximizationState = (state: WindowServiceStore, windowEvent: PhysicalWindowStateEvent, cmd: CMD) => {
	const isMaximized = windowEvent.windowState === "maximized";
	const isResizeEvent = ["resized", "resize"].includes(windowEvent.eventName);

	if (isMaximized && isResizeEvent) {
		const { windowName } = windowEvent;

		const titleBarState = state.windowTitleBar.windows[windowName];
		const physicalState = state.main.windows[windowName].physicalWindowState;

		const notMaximized = titleBarState?.maximized === false;
		const isPhysicallyMaximized = physicalState.windowState === "maximized";

		if (notMaximized && isPhysicallyMaximized) {
			titleBarState.maximized = true;
		}
	}
};

/**
 * Runs reactive effects for window groups ("stuck" windows).
 *
 * If a window in a group is minimized, then all the other windows in that group are minimized, unless they are hidden tabs.
 * Note that operating systems will only minimize one window at a time.
 *
 * If any window in a group is restored from minimization then the other windows are also restored.
 *
 * Note: Most group related window management is still handled in docking (movement, maximization)
 *
 */
const maybeRunReactiveGroupEffects = (state: WindowServiceStore, windowEvent: PhysicalWindowStateEvent, cmd: CMD) => {
	const { windowName } = windowEvent;
	if (windowEvent.eventName === "minimize") {
		const peers = getPeers(state, windowName);
		peers
			// only minimize non-minimized windows because win32 sends minimized events if a minimized window is minimized, which can cause an endless loop
			.filter(
				(w) =>
					state.main.windows[w]?.physicalWindowState?.windowState !== "minimized" &&
					state.main.windows[w]?.physicalWindowState?.windowState !== "minimized_from_maximized"
			)
			.forEach((w) => {
				cmd.run(sendCommandToWindowService, {
					args: [w, "minimize"],
				});
			});
	}

	// Note, this will only get called when restoring from minimized state. Electron sends the "unmaximize" event when restoring from maximized event.
	if (windowEvent.eventName === "restore") {
		const peers = getPeers(state, windowName);
		peers
			// only restore minimized windows because win32 sends restored events if a window is restored, which can cause an endless loop
			.filter(
				(w) =>
					state.main.windows[w]?.physicalWindowState?.windowState == "minimized" ||
					state.main.windows[w]?.physicalWindowState?.windowState === "minimized_from_maximized"
			)
			.forEach((w) => {
				cmd.run(sendCommandToWindowService, {
					args: [w, "restore"],
				});
			});
	}
};

/**
 * Whenever a window is moved or resized (including from a maximize effect) we proactively set the bounds of
 * the hidden windows to match. This prevents flashes of white that might otherwise occur if we waited until
 * the hidden tabs were promoted.
 *
 * We use a throttling function to prevent a heavy load on the system.
 */
const maybeRunReactiveEffects = (state: WindowServiceStore, windowEvent: PhysicalWindowStateEvent, cmd: CMD) => {
	const { windowName } = windowEvent;

	const resized = windowEvent.eventName === "resized" || windowEvent.eventName === "resize";
	const moved = windowEvent.eventName === "moved" || windowEvent.eventName === "move";
	if (!moved && !resized) return;

	// Maximizing creates three events: maximize, resize, and move. We can ignore the move event since it's redundant.
	if (moved && windowEvent.windowState === "maximized") return;

	const collection = getTabCollection(state, windowName);
	// If a single window receives a resize event with bounds that match its normal bounds then we must be unmaximizing
	if (
		resized &&
		!collection &&
		!identicalBounds(windowEvent.bounds, state.main.maximization[windowName]?.maximizedBounds)
	) {
		if (state.windowTitleBar.windows[windowName]) state.windowTitleBar.windows[windowName].maximized = false;
		delete state.main.maximization[windowName];
	}

	// If the promoted window is emulated maximization then this window must also be emulated maximization
	// It's possible that it was formerly actually maximized and now being resized to emulated dimensions
	if (
		resized &&
		collection &&
		collection.promotedWindowName !== windowName &&
		state.windowTitleBar.windows[collection.promotedWindowName].maximized
	) {
		state.main.maximization[windowName] = {
			...state.main.maximization[collection.promotedWindowName],
		};
	}

	// Don't run reactive effects on resize events on hidden windows in a collection
	if (collection?.promotedWindowName !== windowName) return;

	/**
	 * If we receive a resize event on the promoted window that is the same as our normalBounds (emulated or real) then
	 * this resize must have been due to an unmaximization. Unset the maximize state for the collection and remove all
	 * emulated maximizations on windows in the collection.
	 *
	 * Note: it's possible for a single window to be in emulated maximization if all the other tabs
	 * have been removed from the collection leaving it as the only remaining window.
	 */
	if (resized && !identicalBounds(windowEvent.bounds, state.main.maximization[windowName]?.maximizedBounds)) {
		collection?.tabs.forEach((tab) => {
			// Unmaximize all the windows in the collection since we know now that we're unmaximizing
			if (state.windowTitleBar.windows[tab]) state.windowTitleBar.windows[tab].maximized = false;
			delete state.main.maximization[tab];
		});
	}
	if (windowEvent.bounds) {
		cmd.run(throttledSetBounds, {
			args: [windowName, windowEvent.bounds, windowEvent.windowState],
		});
	}
};

/**
 * Sets the title to display in the taskbar. Works only for WebWindow.
 * @param windowName  {string} The current window name
 * @param title {string} The actual title to display
 */
const setTaskbarTitle = async (windowName: string, title: string) => {
	const finWindow = System.Window.wrap(windowName);
	if (finWindow) {
		finWindow.setTaskbarTitle(title);
	}
};

/**
 * Sets the actual title to display in the tab. In order of precedence:
 * 1) The user edited title
 * 2) Title set by an API call
 * 3) Title set in config under foreign.["Window Management"].title
 * 4) The document title
 * 5) The AppD title
 *
 * Tabs are editable if manifest.finsemble.servicesConfig.window.allowWindowTitleRenaming is set to true
 */
export const setTabDisplayTitle = (state: WindowServiceStore, windowName: string, cmd: CMD) => {
	// If the window is registered with windowTitleBar slice then update its title. This is what will update the react rendering.
	const currentTitlebarState = state.windowTitleBar.windows[windowName];
	if (!currentTitlebarState) return;
	if (state.main.windows[windowName]) {
		const { titles = {} } = state.main.windows[windowName];
		currentTitlebarState.title =
			titles.titleFromUser ??
			titles.titleFromAPI ??
			titles.titleFromConfig ??
			titles.titleFromDocument ??
			titles.titleFromAppD ??
			null;

		// setTaskbarTitle works only for WebWindow, for others it throws an error.
		if (currentTitlebarState.title && state.main.launchData[windowName].windowDescriptor.windowType === "WebWindow") {
			cmd.run(setTaskbarTitle, {
				args: [windowName, currentTitlebarState.title],
			});
		}
	}
};

/**
 * Instructs the docking system to swap the groups for each pair of windows. This is used when a new tab has been promoted, so that the old tab window's
 * position in the group is replaced with the newly promoted tab's window.
 * Then provide the latest store state to the workspace service. The workspace service will use this to
 * update the active workspace (e.g. tab collections).
 */
const swapAndUpdateWorkspace = async (
	swaps: Record<string, string>,
	mainState: WindowServiceMainStore,
	titlebarState: WindowTitleBarStore
) => {
	await RouterClient.query("DockingService.swapWindows", swaps);

	// This sends the entire store over to the workspace service. Here we slim it down by eliminating some extraneous data.
	await RouterClient.query("WorkspaceService.updateFromWindowStore", {
		main: {
			...mainState,
			"@@cmds": {},
			launchData: {},
		},
		windowTitleBar: {
			...titlebarState,
		},
	});
};

/**
 * Keeps external systems synchronized with changes to the store. This includes updating groups, so that
 * the promoted tab in a collection is always part of the group. This also includes updating the active
 * workspace.
 */
export const updateGroupsAndWorkspace = (state: WindowServiceStore, cmd: CMD) => {
	let swaps: Record<string, string> = {};

	Object.entries(state.main.groups).forEach(([, groupData]) => {
		groupData.windowNames.forEach((windowName) => {
			const collection = state.windowTitleBar.windows[windowName]?.tabCollectionId ?? null;
			if (!collection) return;
			const promotedWindowName = state.windowTitleBar.tabCollections[collection]?.promotedWindowName;
			if (promotedWindowName === windowName) return;
			swaps[windowName] = promotedWindowName;
		});
	});

	cmd.run(swapAndUpdateWorkspace, {
		args: [swaps, unImmer(state.main), unImmer(state.windowTitleBar)],
	});
};

/**
 * Updates the windowTitleBar slice's window entry given a window event. We want to limit the frequency of changes
 * to the windowTitleBar slice (because it is transmitted over remote-redux). Therefore, we only update the entries
 * for windows that actually have title bars, and we only do so when the window event affects the title bar itself,
 * such as aot changes ("always-on-top-changed"), maximized state ("maximized","unmaximize").
 */
const maybeUpdateWindowTitleBar = (state: WindowServiceStore, windowEvent: PhysicalWindowStateEvent, cmd: CMD) => {
	const { windowName } = windowEvent;

	// These are the only events that affect the title bar.
	if (!["always-on-top-changed", "maximize", "unmaximize"].includes(windowEvent.eventName)) return;

	// Only windows with title bars need be affected.
	const currentState: TitleBarData = state.windowTitleBar.windows[windowName];
	if (!currentState) return;

	/**
	 * When a window is maximized we set all the other tabs in the collection to have
	 * emulated maximization, saving their bounds as normal bounds to future restoration.
	 */
	if (windowEvent.eventName === "maximize") {
		if (windowEvent.normalBounds) {
			state.main.maximization[windowName] = {
				normalBounds: windowEvent.normalBounds,
				maximizedBounds: windowEvent.bounds,
				emulated: false,
			};
		}
		const collection = getTabCollection(state, windowName);
		collection?.tabs.forEach((tab) => {
			if (tab === windowName) return;
			state.windowTitleBar.windows[tab].maximized = true;
			state.main.maximization[tab] = {
				normalBounds: windowEvent.normalBounds,
				maximizedBounds: windowEvent.bounds,
				emulated: true,
			};
		});
	}

	if (windowEvent.eventName === "unmaximize") {
		delete state.main.maximization[windowName];
	}

	state.windowTitleBar.windows[windowName] = {
		...currentState,
		alwaysOnTop: windowEvent.isAlwaysOnTop ?? currentState.alwaysOnTop,
		// Don't trust Electron's maximized window state. It will be set to maximized even if the window is merely resized to the full monitor dimensions.
		// Instead, we keep state.main.maximization up to date so that it is reliable
		maximized: !!state.main.maximization[windowName],
	};

	setTabDisplayTitle(state, windowName, cmd);
};

/**
 * Set the internal state's bounds for maximised windows correctly. There is a chromium bug that returns
 * incorrect bounds for a maximized window. Usually -8px. Set the bounds to the available rect of the monitor
 */
const maybeUpdateMaximizedBounds = async (
	state: WindowServiceStore,
	windowEvent: PhysicalWindowStateEvent,
	cmd: CMD
) => {
	/**
	 * This array should technically only be "resize" and "maximize" but technically any events could update the
	 * maximized bounds. I had to add "always-on-top-changed" for it to work only local as that was triggering and
	 * updating the bounds before tabbing. There may be other conditions where other events would need to be added
	 * to this.
	 */
	if (
		["resizing", "maximize", "always-on-top-changed"].includes(windowEvent.eventName) &&
		windowEvent.windowState == "maximized"
	) {
		const bounds = await getMaximizedBounds(windowEvent.normalBounds);
		const store = getStore();

		store?.dispatch(actions.updateMaximizedBounds({ windowName: windowEvent.windowName, bounds }));
	}
};

/**
 * Removes a tab from its current tab collection.
 */
export const removeFromTabCollection = ({
	state,
	windowName,
	cmd,
}: {
	state: WindowServiceStore;
	windowName: string;
	cmd: CMD;
}) => {
	// Get the tab collection for the target window
	let tabCollection = getTabCollection(state, windowName);
	if (!tabCollection?.tabCollectionId) return;

	// Remember current position (we'll use this to promote another tab)
	const position = tabCollection.tabs.findIndex((tab) => tab === windowName);
	// Remove this window from the collection
	tabCollection.tabs = tabCollection.tabs.filter((tab) => tab !== windowName);

	// Adjust the collection
	if (!tabCollection.tabs.length) {
		// If the remaining collection is empty then remove it
		delete state.windowTitleBar.tabCollections[tabCollection.tabCollectionId];
	} else if (tabCollection.tabs.length === 1) {
		const remainingWindowName = tabCollection.tabs[0];
		// If the remaining collection only has one member then remove it, and set the member window to not point to a collection
		const remainingWindow = state.windowTitleBar.windows[remainingWindowName];
		if (remainingWindow) remainingWindow.tabCollectionId = null;
		delete state.windowTitleBar.tabCollections[tabCollection.tabCollectionId];
		if (!state.main.workspaceClosings.includes(remainingWindowName)) {
			cmd.run(sendCommandToWindowService, {
				args: [remainingWindowName, "show"],
			});
		}
	} else if (tabCollection.promotedWindowName === windowName) {
		// If the remaining collection has more than one member then promote a new tab.
		// This algorithm promotes the tab that was previously to the right of the removed tab.
		// If the removed tab was the last tab then the new last tab will be promoted
		tabCollection.promotedWindowName = tabCollection.tabs[Math.min(position, tabCollection.tabs.length - 1)];
		if (!state.main.workspaceClosings.includes(tabCollection.promotedWindowName)) {
			cmd.run(sendCommandToWindowService, {
				args: [tabCollection.promotedWindowName, "show"],
			});
		}
	}

	// Normalize the window state
	if (state.windowTitleBar.windows[windowName]) {
		state.windowTitleBar.windows[windowName].tabCollectionId = null;
	} else {
		Logger.system.warn(`invariant: removeFromTabCollection can't find window entry for ${windowName}`);
	}
};

/**
 * If the window that was closed is the promoted window in a tab collection then remove the entire collection.
 * This is because a `closed` event received on a promoted window is assumed to mean that the end user
 * manually closed the window, using F4 or by choosing to close the taskbar icon, and therefore expects that
 * the entire collection is to be closed.
 *
 * If the window that was closed is not the promoted window then it is removed from any tab collection that it
 * belongs to.
 */
const maybeRemoveFromTabCollection = ({
	state,
	windowName,
	cmd,
}: {
	state: WindowServiceStore;
	windowName: string;
	cmd: CMD;
}) => {
	// Get the tab collection for the target window
	const tabCollection = getTabCollection(state, windowName);
	if (tabCollection?.promotedWindowName === windowName && !state.windowTitleBar.windows[windowName]?.toBeEjected) {
		state.windowTitleBar.tabCollections[tabCollection.tabCollectionId].tabs.forEach((tab) => {
			cmd.run(sendCommandToWindowService, {
				args: [tab, "close", { removeFromWorkspace: true }],
			});
		});
		delete state.windowTitleBar.tabCollections[tabCollection.tabCollectionId];
	} else {
		removeFromTabCollection({ state, windowName, cmd });
	}
};

/**
 * Shows one window and then hides the other. This is used when changing the promoted tab. We wait for the lower window to be shown
 * before hiding the upper window to prevent white flashes (which mostly show up when switching between tabbed windows in maximized mode).
 *
 * The extra bringToFront is necessary to prevent the newly promote tab from remaining occluded on MS Windows.
 */
export const showThenHide = async (showWindow: string, hideWindow: string) => {
	await sendCommandToWindowService(showWindow, "show");
	await sendCommandToWindowService(showWindow, "bringToFront");
	await sendCommandToWindowService(hideWindow, "hide");
};

/**
 * Promotes the given window in its collection, if it's in a collection
 */
export const promoteWindow = (state: WindowServiceStore, windowName: string) => {
	let targetWindowNameTabCollection = getTabCollection(state, windowName);
	if (targetWindowNameTabCollection) targetWindowNameTabCollection.promotedWindowName = windowName;
};

/**
 * Get the user defined title if it exists. The user defined title comes from `persistedTitle`
 * in workspace data or is set as a spawn param with workspace data having higher priority.
 */
const getUserEditedTabNameIfAny = async (windowName: string, tabTitleFromSpawn?: string) => {
	/**
	 * This function runs before a window has finished being created in the window service. We therefore
	 * cannot use getComponentState that runs through FinsembleWindow. Meanwhile, WorkspaceClient doesn't
	 * have such a non-deprecated function for getting component state either. Neither is there a generic
	 * internal function that can be shared by all. Therefore, we go directly to storage for the component
	 * state. Note that by having component state available before the window is created we avoid flashing
	 * the app's default title prior to the user edited title being displayed.
	 */
	const { data: componentState } = await StorageClient.getStandardized({
		topic: "finsemble.workspace.cache",
		key: camelCase("activeWorkspace", windowName, windowName),
	});
	return {
		windowName,
		title: componentState?.persistedTitle ?? tabTitleFromSpawn ?? null,
	};
};

/**
 * Runs through the current groups and sets the windowTitleBars' groupable and isGrouped status for all windows.
 *
 * This function is called when updateGroupedWindows is received from the docking system, indicating
 * that a window has been grouped or ungrouped.
 *
 * This function is also called whenever a window registers, because groups may be set ahead of time
 * by a workspace load, requiring that newly opened windows fit into those pre-existing groups
 */
export const updateGroupStatusForAllTitleBars = (state: WindowServiceStore) => {
	// We don't get any notices that a group has been removed, so first
	// set _all_ windows to to not be grouped, not be groupable, and have a default minimize button.
	Object.entries(state.windowTitleBar.windows).forEach(([windowName, value]) => {
		value.groupable = false;
		value.isGrouped = false;
		value.showMinimizeButton = getPermission(state, windowName, "minimize");
	});

	// Next, set groupable to true for any windows in a group if all are groupable
	// If A is snapped to B and B is not groupable, we don't want to display a grouping icon on A
	// console.error("updateGroupStatusForAllTitleBars", state.main.groups)
	Object.values(state.main.groups).forEach((windowGroup) => {
		const isGroupGroupable = windowGroup.windowNames.every((windowName) =>
			getPermission(state, windowName, "grouping")
		);
		windowGroup.windowNames.forEach((windowName) => {
			if (state.windowTitleBar.windows[windowName]) {
				state.windowTitleBar.windows[windowName].groupable = isGroupGroupable;
			}
		});
		if (!windowGroup.isGrouped) return;

		// Finally, turn on the grouped state for any windows in actual groups (and turn off showMinimizeIcon where necessary)
		windowGroup.windowNames.forEach((windowName) => {
			if (state.windowTitleBar.windows[windowName]) {
				state.windowTitleBar.windows[windowName].isGrouped = true;
				state.windowTitleBar.windows[windowName].showMinimizeButton = windowGroup.topRightWindows.includes(windowName);
			}
		});
	});
};

const addReducerCases = (builder: ActionReducerMapBuilder<WindowServiceStore>) => {
	builder
		/**
		 * The docking system provides wholesale updates describing all the windows that are snapped into "potential" groups
		 * as well as all windows that are formed into actual groups.
		 * We must parse through this update and set each window's state depending on the contents.
		 *
		 * For every formed group there also remains a potential snapped group. We must be careful with the results.
		 */
		.addCase(actions.updateGroupedWindows, (state, action, cmd) => {
			state.main.groups = action.payload;
			updateGroupStatusForAllTitleBars(state);
			updateGroupsAndWorkspace(state, cmd);
		})
		/**
		 * Called after the user edited title has been initially loaded from workspace state. Since this runs as a result
		 * of an asynchronous call to storage, it is possible that it may run either before or after the actual window is
		 * loaded. For this reason, we update both the launchData and the actual window data.
		 */
		.addCase(actions.loadUserDefinedTitle, (state, action, cmd) => {
			const { title, windowName } = action.payload;
			if (title && state.main.launchData[windowName]) {
				state.main.launchData[windowName].userDefinedTitle = title;
			}
			if (state.main.windows[windowName]) {
				state.main.windows[windowName].titles.titleFromUser = title;
				setTabDisplayTitle(state, windowName, cmd);
			}
		})
		/**
		 * Called from launcher whenever a window is being spawned. This is called before the actual
		 * spawn event, so that the store has all the launching information by the time the window
		 * open event is received.
		 */
		.addCase(actions.addLaunchData, (state, action, cmd) => {
			let windowDescriptorFull: WindowDescriptor = unImmer(action.payload.windowDescriptor);
			// The WindowDescriptor is bloated. Delete unnecessary stuff to lessen the store size.
			delete windowDescriptorFull?.customData?.manifest;
			delete windowDescriptorFull?.permissions;
			delete windowDescriptorFull?.manifest;
			delete windowDescriptorFull?.childWindowOptions;

			const windowDescriptor: WindowDescriptorScrubbed = windowDescriptorFull;

			const { workspaceWindow, tabTitleFromSpawn, windowDotOpen } = action.payload;
			// Note that window descriptors are currently around ~15k. Each one has a full copy of the manifest which is ~9k.
			// We might want to consider deleting the manifest before it gets in the store
			if (windowDescriptor.name) {
				if (state.main.launchData[windowDescriptor.name]) {
					// Native windows will flow through launcher.ts addExternallyOpenedWindow() even if they are locally
					// launched, causing us to overwrite the descriptor.
					return;
				}
				state.main.launchData[windowDescriptor.name] = {
					windowDescriptor,
					workspaceWindow,
					windowDotOpen,
				};
				if (windowDescriptor?.customData?.foreign?.components?.["Window Manager"]?.FSBLHeader) {
					cmd.run(getUserEditedTabNameIfAny, {
						args: [windowDescriptor.name, tabTitleFromSpawn],
						successActionCreator: actions.loadUserDefinedTitle,
					});
				}
				if (windowDotOpen) {
					// Windows that were opened through a call to window.open() will already exist. Now is the time to register with docking.
					cmd?.run(maybeRegisterWithDocking, {
						args: [windowDescriptor.name, unImmer(windowDescriptor)],
					});
				}
			}
			state.finsembleConfig = unImmer(action.payload.finsembleConfig);
		})
		/**
		 * Called after a window finishes spawning to save a user defined title specified during spawn to the workspace.
		 * The tab Display title will have already been updated at this point (we don't do it here so the title will update without flashing)
		 * We only do this save if the tab Display title was set to the spawn value instead of a user defined title saved in the workspace.
		 */
		.addCase(actions.saveSpawnTabTitleToWorkspace, (state, action) => {
			const { tabTitle, windowName } = action.payload;
			if (tabTitle === state.main.windows[windowName]?.titles?.titleFromUser) {
				setUserDefinedTitle(windowName, tabTitle);
			}
		})
		/**
		 * Turns on the ShareButton if DragNDrop is enabled
		 */
		.addCase(actions.enableDND, (state, action) => {
			const { targetWindowName } = action.payload;
			if (state.windowTitleBar.windows[targetWindowName]) {
				state.windowTitleBar.windows[targetWindowName].showShareButton = action.payload.toggleState;
			}
		})
		/**
		 * Updates the current docking operation. This is called from dockingMain.
		 */
		.addCase(actions.dockingOperation, (state, action) => {
			state.main.dockingOperation = action.payload;
		})
		/**
		 * Called from within each app whenever document.title changes. windowStartup contains
		 * a MutationObserver that does this work.
		 */
		.addCase(actions.setDocumentTitle, (state, action, cmd) => {
			const { windowName } = action["@@remote"];
			const { title } = action.payload;
			const currentState = state.main.windows[windowName];
			if (currentState) {
				state.main.windows[windowName].titles = {
					...currentState.titles,
					titleFromDocument: title,
				};

				setTabDisplayTitle(state, windowName, cmd);
			}
		})
		/**
		 * Called whenever WindowClient.setWindowTitle() is used.
		 */
		.addCase(actions.setAPITitle, (state, action, cmd) => {
			const { windowName } = action["@@remote"];
			const { title } = action.payload;
			const currentState = state.main.windows[windowName];
			if (currentState) {
				state.main.windows[windowName].titles = {
					...currentState.titles,
					titleFromAPI: title,
				};

				setTabDisplayTitle(state, windowName, cmd);
			}
		})
		/**
		 * Called from the workspace service to instantiate tab collections. This gets called before the windows
		 * for the workspace are opened, so that the framework exists for the windows to settle into as they come
		 * on line.
		 */
		.addCase(actions.loadWorkspace, (state, action) => {
			const { tabCollections } = action.payload;

			const newCollection = Object.fromEntries(
				tabCollections.map((tabCollection) => [tabCollection.tabCollectionId, tabCollection])
			);

			// updates tabCollections if there are changes
			if (!isEqualIgnoringArrayOrder(state.windowTitleBar.tabCollections, newCollection)) {
				/**
				 * In one swoop we replace all the tab collections with the new ones from the workspace we're
				 * loading. Window references will be deleted naturally when those window closed events are received but
				 * we'll delete the tabCollectionId references just for data integrity since those tab collections no longer exist.
				 */
				Object.values(state.windowTitleBar.windows).forEach((w) => (w.tabCollectionId = null));
				state.windowTitleBar.tabCollections = newCollection;
			}
		})
		/**
		 * Called from workspace service when initializing maximized windows. FEA windows that re to be re-hydrated
		 * in maximized state are always emulated maximization. Native windows are truly maximized.
		 */
		.addCase(actions.setEmulatedMaximization, (state, action) => {
			const { targetWindow, normalBounds, maximizedBounds } = action.payload;
			state.main.maximization[targetWindow] = {
				normalBounds,
				maximizedBounds,
				emulated: true,
			};
			const win = state.windowTitleBar.windows[targetWindow];
			if (win) win.maximized = true;
		})
		/**
		 * Called by the workspace service when a workspace is being closed. This adds the window names
		 * of every window that is being closed. Reducers can use this list to bypass running effects
		 * for those windows.
		 */
		.addCase(actions.workspaceClosing, (state, action) => {
			state.main.workspaceClosings = state.main.workspaceClosings.concat(action.payload);
		})
		/**
		 * Processes WindowEventStream events. These events originate in either FEA or Assimilation, indicating each
		 * time the state of a window has changed. The windowService fields these events and then passes them into this
		 * reducer for processing.
		 *
		 * Idiosyncrasies to note:
		 * - A window that is resized to maximized size (but not actually maximized) will still show isMaximized() according to Electron
		 * - Showing such a window will cause Electron to set that window's normalBounds to the maximized size
		 */
		.addCase(actions.windowEvent, (state, action, cmd) => {
			const { windowName } = action.payload;
			const windowDescriptor = state.main.launchData[windowName]?.windowDescriptor ?? null;
			if (action.payload.eventName === "closed") {
				maybeRemoveFromTabCollection({ state, windowName, cmd });
				delete state.windowTitleBar.windows[windowName];
				delete state.main.windows[windowName];
				delete state.main.launchData[windowName];
				delete state.main.maximization[windowName];
				cmd.run(unregisterWithDocking, {
					args: [windowName],
				});
				cmd.run(unsubscribeFromDNDUpdate, {
					args: [windowName],
				});
				if (!state.main.workspaceClosings.includes(windowName)) {
					updateGroupsAndWorkspace(state, cmd);
				}
				state.main.workspaceClosings = state.main.workspaceClosings.filter((name) => name !== windowName);
				return;
			}
			state.main.windows[windowName] = {
				titles: {
					titleFromConfig: windowDescriptor?.customData?.foreign?.components?.["Window Manager"]?.title ?? null,
					titleFromAppD: windowDescriptor?.customData?.appConfig?.title ?? null,
					titleFromUser: state.main.launchData[windowName]?.userDefinedTitle ?? null,
					...state.main.windows[windowName]?.titles,
				},
				physicalWindowState: {
					...action.payload,
				},
			};

			maybeUpdateMaximizedBounds(state, action.payload, cmd);
			maybeUpdateWindowTitleBar(state, action.payload, cmd);
			maybeRunReactiveEffects(state, action.payload, cmd);
			maybeRunReactiveGroupEffects(state, action.payload, cmd);
			// keeps the same titlebar.maximized and physical.maximized state for monitors-changed event
			// which invokes the "resize" event for all windows
			maybeUpdateMaximizationState(state, action.payload, cmd);

			if (action.payload.eventName === "opened") {
				if (windowDescriptor) {
					cmd?.run(maybeRegisterWithDocking, {
						args: [windowName, unImmer(windowDescriptor)],
					});
				}
				cmd?.run(listenForDNDUpdates, {
					args: [windowName],
				});
			}
		})
		/**
		 * Set the internal state's bounds for maximised windows correctly. There is a chromium bug that returns
		 * incorrect bounds for a maximized window. Usually -8px.
		 */
		.addCase(actions.updateMaximizedBounds, (state, action, cmd) => {
			state.main.windows[action.payload.windowName].physicalWindowState.bounds = action.payload.bounds;
		})
		/**
		 * Called by launcher before component spawning to initialize the WindowTitleBar state
		 */
		.addCase(actions.createDefaultWindowTitleBar, (state, action) => {
			const windowName = action.payload;
			// creating a "placeholder" entry for the window TitleBar before spawn
			state.windowTitleBar.windows[windowName] = {
				title: null,
				editable: false,
				groupable: false,
				isGrouped: false,
				tabCollectionId: null,
				enableTabbing: getPermission(state, windowName, "tabbing"),
				showMaximizeButton: getPermission(state, windowName, "maximize"),
				showMinimizeButton: getPermission(state, windowName, "minimize"),
				showAlwaysOnTopButton: getPermission(state, windowName, "alwaysOnTop"),
				showCloneButton: getPermission(state, windowName, "clone"),
				showCloseButton: getPermission(state, windowName, "close"),
				showShareButton: false,
				bumpConfig: null,
				icon: null,
				alwaysOnTop: false,
				maximized: false,
				toBeEjected: false,
			};
		})
		/**
		 * Called by the workspace service to update evergreen window names in tabCollection.
		 * It happens when the evergreen app has a "win-1" window name in the workspace, but the actual wrapper in "win-2".
		 */
		.addCase(actions.updateEvergreenAfterWorkspaceLoading, (state, action) => {
			const evergreenNamesMap = action.payload;
			const keys = Object.keys(evergreenNamesMap);
			if (keys.length === 0) return;

			let tabCollectionsStr = JSON.stringify(state.windowTitleBar.tabCollections);

			keys.forEach((key) => {
				const newName = evergreenNamesMap[key];
				if (state.windowTitleBar.windows[newName]) {
					tabCollectionsStr = tabCollectionsStr.replace(new RegExp(key, "g"), newName);
				}
			});

			state.windowTitleBar.tabCollections = JSON.parse(tabCollectionsStr);
		})
		/**
		 * Called by the workspace service when an evergreen window has been shown after workspace reloading.
		 * This adds the tabCollectionId to corresponding window in the store.
		 * The tabCollectionId is removed for evergreen in actions.loadWorkspace.
		 */
		.addCase(actions.setTabCollectionForEvergreen, (state, action, cmd) => {
			const { windowName, tabCollectionId } = action.payload;

			if (!tabCollectionId) removeFromTabCollection({ state, windowName, cmd });

			const tabWindow = state.windowTitleBar.windows[windowName];
			if (tabWindow) tabWindow.tabCollectionId = tabCollectionId ?? null;
		});
};

export default {
	getInitialState,
	addReducerCases,
	actions,
};
